import {
  type SendToServerEvent,
  type WebSocketStore,
  type WebsocketMachineStates,
  type WsEvents,
  newConnectedEvent,
  newDisconnectedEvent,
  newOnReceiveError,
  newReceiveFromServerEvent,
} from "@core/domains/ws/machines";
import { APIError, ErrCode, getClientTarget } from "@repo/client";
import { type Logger, Named } from "@repo/logger";
import type { useActor } from "@xstate/solid";

export type SSEServiceDependencies = {
  actor: ReturnType<typeof useActor>;
  logger: Logger;
  getAuthToken: () => string | undefined;
  isIdentityConnecting: () => boolean;
};

export interface SSEService {
  snapshot: {
    value: WebsocketMachineStates;
    context: WebSocketStore;
  };
  send: ReturnType<typeof useActor>[1];
  actor: ReturnType<typeof useActor>[2];
  connect: () => void;
  disconnect: () => void;
}

export const useSSEService = (deps: SSEServiceDependencies): SSEService => {
  const [snapshot, send, actor] = deps.actor;
  const logger = new Named(deps.logger, "sseService");
  actor.start();

  // Track active fetch requests
  const activeRequests = new Map<string, AbortController>();
  let connected = false;

  // Helper method to check if a parsed JSON object is an error response
  const isErrorResponse = (data: unknown): boolean => {
    if (!data || typeof data !== "object") {
      return false;
    }

    const dataObj = data as Record<string, unknown>;

    // Check for explicit error indicators
    if (dataObj.message === "Error" && dataObj.code) {
      return true;
    }

    // Check for error response with base64 encoded data
    if (dataObj.message === "Error" && dataObj.data && typeof dataObj.data === "string") {
      try {
        const decoded = atob(dataObj.data as string);
        const decodedData = JSON.parse(decoded);
        return (
          decodedData &&
          typeof decodedData === "object" &&
          (decodedData.code || decodedData.message || decodedData.details)
        );
      } catch (e) {
        // If base64 decode fails, still treat as error if message is "Error"
        return true;
      }
    }

    return false;
  };

  // Helper method to convert a proper error response to APIError
  const convertErrorResponse = (errorResponse: unknown): APIError => {
    const errorObj = errorResponse as Record<string, unknown>;
    const details = errorObj.details as Record<string, unknown> | undefined;
    const operation = details?.operation as Record<string, unknown> | undefined;
    let errType = operation?.errType as string | undefined;
    let actualErrorData = null;

    // Check if we have base64-encoded error data that needs to be decoded
    if (errorObj.data && typeof errorObj.data === "string" && !errType) {
      try {
        const decoded = atob(errorObj.data as string);
        actualErrorData = JSON.parse(decoded);
        errType = actualErrorData.details?.operation?.errType;
      } catch (e) {
        logger.warn("Failed to decode base64 error data", {
          error: e instanceof Error ? e.message : String(e),
          dataPreview: typeof errorObj.data === "string" ? errorObj.data.substring(0, 100) : "",
        });
      }
    }

    // Use the decoded error data if available, otherwise fall back to the original response
    const errorDetails = actualErrorData?.details || errorObj.details;

    // Extract the user-friendly message from the backend response
    const backendMessage = actualErrorData?.message || errorObj.message;
    const userMessage =
      typeof backendMessage === "string" && backendMessage.length > 0
        ? backendMessage
        : "An unexpected server error occurred";

    // Use the error code provided by the backend (backend handles proper error code mapping)
    const backendCode = actualErrorData?.code || errorObj.code;
    const errorCode = backendCode || ErrCode.Internal;

    // Map error codes to HTTP status codes (standard HTTP status mapping)
    const getStatusFromCode = (code: ErrCode): number => {
      switch (code) {
        case ErrCode.InvalidArgument:
          return 400;
        case ErrCode.PermissionDenied:
          return 403;
        case ErrCode.NotFound:
          return 404;
        case ErrCode.ResourceExhausted:
          return 429;
        case ErrCode.FailedPrecondition:
          return 400;
        default:
          return 500;
      }
    };

    return new APIError(getStatusFromCode(errorCode), {
      code: errorCode,
      message: userMessage,
      details: errorDetails,
    });
  };



  // Simple method to indicate connection status
  const connect = () => {
    if (!connected) {
      connected = true;
      send(newConnectedEvent());
    }
  };

  const disconnect = () => {
    // Abort all active requests
    activeRequests.forEach((controller, id) => {
      controller.abort();
      activeRequests.delete(id);
    });

    connected = false;
    send(newDisconnectedEvent());
  };

  // Process a command by sending it to the server and processing the stream
  const processCommand = async (envelope: SendToServerEvent["envelope"]) => {
    const token = deps.getAuthToken();
    if (!token) {
      logger.warn("No token available for sending command");
      return;
    }

    // Generate a unique ID for this command
    const commandId = Date.now().toString(36);

    // Create an AbortController for this request
    const abortController = new AbortController();
    activeRequests.set(commandId, abortController);

    try {
      const target = getClientTarget();
      logger.info("Sending command to stream endpoint", { commandId, envelope });

      const response = await fetch(`${target}/v1/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(envelope),
        signal: abortController.signal,
      });

      if (!response.ok) {
        let apiError: APIError;

        switch (response.status) {
          case 400:
            apiError = new APIError(400, {
              code: ErrCode.InvalidArgument,
              message: "Invalid request format or parameters",
              details: { status: response.status, statusText: response.statusText },
            });
            break;
          case 401:
            apiError = new APIError(401, {
              code: ErrCode.Unauthenticated,
              message: "Authentication required. Please log in again.",
              details: { status: response.status, statusText: response.statusText },
            });
            break;
          case 403:
            apiError = new APIError(403, {
              code: ErrCode.PermissionDenied,
              message: "You don't have permission to perform this action",
              details: { status: response.status, statusText: response.statusText },
            });
            break;
          case 404:
            apiError = new APIError(404, {
              code: ErrCode.NotFound,
              message: "The requested resource was not found",
              details: { status: response.status, statusText: response.statusText },
            });
            break;
          case 429:
            apiError = new APIError(429, {
              code: ErrCode.ResourceExhausted,
              message: "Too many requests. Please wait before trying again.",
              details: { status: response.status, statusText: response.statusText },
            });
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            apiError = new APIError(response.status, {
              code: ErrCode.Internal,
              message: "Server error. Please try again later.",
              details: { status: response.status, statusText: response.statusText },
            });
            break;
          default:
            apiError = new APIError(response.status, {
              code: ErrCode.Unknown,
              message: `HTTP error ${response.status}: ${response.statusText}`,
              details: { status: response.status, statusText: response.statusText },
            });
        }

        logger.error("HTTP_ERROR", "HTTP error response from stream endpoint", new Error(`HTTP ${response.status}: ${response.statusText}`), {
          status: response.status,
          statusText: response.statusText,
          commandId,
          envelope,
        });

        send(newOnReceiveError(apiError));
        return;
      }

      // Get a reader from the response body
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No reader available");
      }

      connect(); // Mark as connected once we establish a stream

      // Process the stream data
      let buffer = "";
      const decoder = new TextDecoder();
      let isFinished = false;

      let lastProcessedTime = 0;

      // Only process new stream data every 150ms to avoid overwhelming the renderer
      const BUFFER_TIME_THRESHOLD = 150;
      const MAX_BUFFER_SIZE = 1024 * 1024; // 1MB max buffer size
      let jsonObjectsProcessed = 0;

      const readLoop = async ({ forceProcessing = false }: { forceProcessing?: boolean } = {}) => {
        const { done, value } = await reader.read();

        if (done) {
          isFinished = true;
          logger.info("Stream ended", {
            commandId,
            totalJsonObjects: jsonObjectsProcessed,
            remainingBuffer: buffer.length,
          });
          return;
        }

        // Decode the chunk and add to buffer
        const chunk = decoder.decode(value, { stream: true });
        const previousBufferSize = buffer.length;
        buffer += chunk;

        // Check for buffer overflow
        if (buffer.length > MAX_BUFFER_SIZE) {
          logger.warn("Buffer overflow detected, truncating", {
            bufferSize: buffer.length,
            maxSize: MAX_BUFFER_SIZE,
            commandId,
          });
          buffer = buffer.slice(-MAX_BUFFER_SIZE / 2); // Keep last half
        }

        if (!forceProcessing && performance.now() - lastProcessedTime < BUFFER_TIME_THRESHOLD) {
          return;
        }

        lastProcessedTime = performance.now();

        // Process the buffer to extract complete JSON objects
        let processedPos = 0;
        let bracketCount = 0;
        let jsonStartPos = -1;

        // Scan through the buffer character by character
        for (let pos = 0; pos < buffer.length; pos++) {
          const char = buffer[pos];

          // Track opening brackets to find JSON object boundaries
          if (char === "{") {
            if (bracketCount === 0) {
              jsonStartPos = pos;
            }
            bracketCount++;
          } else if (char === "}") {
            bracketCount--;

            // When bracket count returns to 0, we have a complete JSON object
            if (bracketCount === 0 && jsonStartPos !== -1) {
              const jsonStr = buffer.substring(jsonStartPos, pos + 1);

              try {
                const parseStartTime = performance.now();
                const data = JSON.parse(jsonStr);
                const parseTime = performance.now() - parseStartTime;

                jsonObjectsProcessed++;

                // Check if this is a proper error response
                if (isErrorResponse(data)) {
                  logger.info("Processing error response", {
                    commandId,
                    errType: data.details?.operation?.errType || "unknown",
                    objectIndex: jsonObjectsProcessed,
                  });
                  const apiError = convertErrorResponse(data);
                  send(newOnReceiveError(apiError));
                } else {
                  // Normal data response
                  send(newReceiveFromServerEvent(data));
                }
              } catch (error) {
                logger.warn("Failed to parse JSON response", {
                  error: error instanceof Error ? error.message : String(error),
                  jsonStr: jsonStr.length > 200 ? `${jsonStr.substring(0, 200)}...` : jsonStr,
                  commandId,
                });

                // Send a fallback error for JSON parse failures
                const fallbackError = new APIError(500, {
                  code: ErrCode.Internal,
                  message: "Failed to parse server response. The server may be experiencing issues.",
                  details: {
                    originalError: error instanceof Error ? error.message : String(error),
                    jsonStr: jsonStr.length > 200 ? `${jsonStr.substring(0, 200)}...` : jsonStr,
                  },
                });
                send(newOnReceiveError(fallbackError));

                // Continue processing the stream instead of aborting
                // Skip this malformed JSON chunk and keep processing
                processedPos = pos + 1;
                continue;
              }

              // Update the processed position
              processedPos = pos + 1;
            }
          }
        }

        // Remove processed JSON objects from buffer, keeping any incomplete JSON
        if (processedPos > 0) {
          buffer = buffer.substring(processedPos);
        }
      };

      while (!isFinished) {
        await readLoop({ forceProcessing: false });
      }

      // Final cleanup pass
      await readLoop({ forceProcessing: true });

      logger.info("Stream processing completed", {
        commandId,
        totalJsonObjects: jsonObjectsProcessed,
      });
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          logger.info("Stream request was aborted", { commandId, envelope });
          // Don't send error for intentional aborts
        } else {
          logger.error("STREAM_CONNECTION_ERROR", "Stream connection error", error, {
            commandId,
            envelope,
          });

          let apiError: APIError;

          // Handle specific connection errors
          if (error.name === "TypeError" && error.message.includes("fetch")) {
            apiError = new APIError(0, {
              code: ErrCode.Unavailable,
              message: "Unable to connect to the server. Please check your internet connection and try again.",
              details: {
                originalError: error.message,
                retryRecommended: true,
                retryDelay: 5000,
              },
            });
          } else if (error.name === "TimeoutError" || error.message.includes("timeout")) {
            apiError = new APIError(0, {
              code: ErrCode.DeadlineExceeded,
              message: "Connection timeout. The server is taking too long to respond.",
              details: {
                originalError: error.message,
                retryRecommended: true,
                retryDelay: 3000,
              },
            });
          } else if (error.message.includes("network") || error.message.includes("Network")) {
            apiError = new APIError(0, {
              code: ErrCode.Unavailable,
              message: "Network error occurred. Please check your connection and try again.",
              details: {
                originalError: error.message,
                retryRecommended: true,
                retryDelay: 5000,
              },
            });
          } else {
            apiError = new APIError(0, {
              code: ErrCode.Unknown,
              message: "An unexpected connection error occurred. Please try again.",
              details: {
                originalError: error.message,
                retryRecommended: true,
                retryDelay: 10000,
              },
            });
          }

          send(newOnReceiveError(apiError));
          send(newDisconnectedEvent());
        }
      } else {
        logger.error("UNKNOWN_ERROR", "Unknown error type in stream connection", error, { commandId, envelope });
        const apiError = new APIError(0, {
          code: ErrCode.Unknown,
          message: "An unexpected error occurred during connection.",
          details: {
            originalError: String(error),
            retryRecommended: true,
            retryDelay: 10000,
          },
        });
        send(newOnReceiveError(apiError));
        send(newDisconnectedEvent());
      }
    } finally {
      activeRequests.delete(commandId);
    }
  };

  // Helper function to find the end of a JSON object in a string
  const findJsonObjectEnd = (str: string, startPos: number): number => {
    let pos = startPos;
    while (pos < str.length && /\s/.test(str[pos] || "")) pos++; // Skip whitespace

    if (pos >= str.length || str[pos] !== "{") return -1; // Must start with {

    let depth = 1;
    pos++; // Move past the first {

    while (pos < str.length && depth > 0) {
      if (str[pos] === "{") depth++;
      else if (str[pos] === "}") depth--;
      pos++;
    }

    return depth === 0 ? pos - 1 : -1;
  };

  return {
    snapshot,
    send: (event: WsEvents) => {
      // If this is a send event, process the command
      if (event.type === "ws.send" && event.envelope) {
        processCommand(event.envelope);
      }

      // Forward the event to the state machine
      send(event);
    },
    actor,
    connect,
    disconnect,
  };
};
